import { useState, useEffect, useRef } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { TargetingController } from '../api/controllers/targeting.controller';
import type { Target } from '../types/campaign';

interface LocationSearchProps {
  onSelect: (location: Target) => void;
  selectedTargets: Target[];
}

export default function LocationSelector({ onSelect, selectedTargets }: LocationSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Target[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value: string) => {
    setQuery(value);
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmedValue = query.trim();

    debounceRef.current = setTimeout(async () => {
      if (trimmedValue.length < 2) {
        setResults([]);
        setError('');
        setLoading(false);
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const locations = await TargetingController.searchLocations(trimmedValue);
        if (currentRequestId !== requestIdRef.current) return;

        const selectedNames = new Set(selectedTargets.map((loc) => (loc.name || '').toLowerCase()));
        const filtered = locations.filter((loc) => !selectedNames.has(loc.name.toLowerCase()));
        setResults(filtered);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        setError('Unable to search locations right now. Please try again.');
        setResults([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, selectedTargets]);

  const handleSelect = (value: Target) => {
    onSelect(value);
    setQuery('');
    setResults([]);
    setIsFocused(false);
  };

  const trimmedQuery = query.trim();
  const shouldShowDropdown =
    isFocused && (loading || error || trimmedQuery.length >= 2 || results.length > 0);

  return (
    <div ref={searchRef} className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-text-muted" />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setIsFocused(true)}
          className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
          placeholder="Search states, cities, or zip codes..."
        />
        {query && (
          <button
            onClick={() => handleSearch('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-text-muted hover:text-neutral-light" />
          </button>
        )}
      </div>

      {shouldShowDropdown && (
        <div className="absolute z-10 w-full mt-2 bg-brand-surface border border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading && <div className="px-4 py-3 text-sm text-text-muted">Searching...</div>}

          {!loading && error && <div className="px-4 py-3 text-sm text-red-400">{error}</div>}

          {!loading && !error && results.length === 0 && trimmedQuery.length >= 2 && (
            <div className="px-4 py-3 text-sm text-text-muted">
              No locations found. Try another search.
            </div>
          )}

          {!loading &&
            !error &&
            results.map((result) => (
              <button
                key={result.id}
                onClick={() => handleSelect(result)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-brand-hover transition-colors"
              >
                <MapPin className="h-5 w-5 text-text-muted" />
                <div className="text-left">
                  <p className="text-sm text-neutral-light">{result.name}</p>
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
